import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Tooltip } from '@mui/material';
import { Autocomplete, TextField, IconButton, Card } from '@totalsoft/rocket-ui';
import { DOCUMENT_TYPE_WITH_ALLOWED_STATES_QUERY } from '../../../features/documentTypes/components/edit/allowedStates/list/queries';
import { useQuery } from 'hooks/apollo';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';

const CancelOffersListItemComponent = ({
  documentTypes,
  referenceDates,
  reasons,
  item,
  index,
  onDelete,
  onPropertyChanged,
  onLookupPropertyChanged,
  validation
}) => {
  const { t } = useTranslation();
  const { data: documentStateList } = useQuery(DOCUMENT_TYPE_WITH_ALLOWED_STATES_QUERY, {
    variables: {
      id: item.documentTypeId
    },
    notifyOnNetworkStatusChange: true,
    skip: !item.documentTypeId
  });

  return (
    <Card
      disablePadding
      actions={
        <IconButton
          type='delete'
          size='tiny'
          variant='text'
          color='primary'
          tooltip={t('Tooltips.Delete')}
          onClick={_ => {
            onDelete(index);
          }}
        />
      }
    >
      <Grid container spacing={2} px={3} pb={2}>
        <Grid item xs={12} sm={4} lg={3}>
          <Autocomplete
            fullWidth
            label={t('CancelOffers.DocumentType')}
            labelKey='name'
            valueKey='id'
            value={{
              id: item.documentTypeId,
              name: item.documentTypeName
            }}
            isClearable={false}
            isSearchable={true}
            id='documentType'
            options={documentTypes}
            onChange={event => {
              onLookupPropertyChanged('documentTypeId', 'documentTypeName', event.id, event.name, index, 'documentTypes');
            }}
            error={!isValid(validation[index].documentTypeId)}
            helperText={getErrors(validation[index].documentTypeId)}
          />
        </Grid>
        <Grid item xs={12} sm={4} lg={3}>
          <Autocomplete
            fullWidth
            label={t('CancelOffers.DocumentState')}
            labelKey='documentStateName'
            valueKey='documentStateId'
            value={{
              documentStateId: item.stateId,
              documentStateName: item.stateName
            }}
            isClearable={false}
            isSearchable={true}
            id='state'
            options={documentStateList?.documentTypeWithAllowedStates?.allowedStates}
            onChange={event => {
              onLookupPropertyChanged('stateId', 'stateName', event.documentStateId, event.documentStateName, index, 'documentStates');
            }}
            error={!isValid(validation[index].stateId)}
            helperText={getErrors(validation[index].stateId)}
          />
        </Grid>
        <Grid item xs={12} sm={4} lg={3}>
          <TextField
            isNumeric
            fullWidth
            label={t('CancelOffers.NormalExpiryPeriod')}
            value={item.normalExpiryPeriod || ''}
            decimalScale={0}
            onChange={event => onPropertyChanged('normalExpiryPeriod', event, index)}
            error={!isValid(validation[index].normalExpiryPeriod)}
            helperText={getErrors(validation[index].normalExpiryPeriod)}
          />
        </Grid>
        <Grid item xs={12} sm={4} lg={3}>
          <TextField
            isNumeric
            fullWidth
            label={t('CancelOffers.ExtendedExpiryPeriod')}
            decimalScale={0}
            value={item.extendedExpiryPeriod || ''}
            onChange={event => onPropertyChanged('extendedExpiryPeriod', event, index)}
            error={!isValid(validation[index].extendedExpiryPeriod)}
            helperText={getErrors(validation[index].extendedExpiryPeriod)}
          />
        </Grid>
        <Grid item xs={12} sm={4} lg={3}>
          <Tooltip placement='top-start' title={t('CancelOffers.ReferenceDateTooltip')}>
            <Autocomplete
              fullWidth
              label={t('CancelOffers.ReferenceDate')}
              labelKey='name'
              valueKey='code'
              value={item.referenceDate}
              simpleValue
              isSearchable={true}
              options={referenceDates}
              onChange={event => {
                onLookupPropertyChanged(null, 'referenceDate', null, event, index, 'referenceDates');
              }}
              error={!isValid(validation[index].referenceDate)}
              helperText={getErrors(validation[index].referenceDate)}
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={4} lg={3}>
          <Autocomplete
            fullWidth
            label={t('CancelOffers.ReasonCode')}
            isClearable={true}
            isSearchable={true}
            labelKey='reasonName'
            valueKey='reasonId'
            value={
              item.reasonId
                ? {
                    reasonId: item.reasonId,
                    reasonName: item.reasonName
                  }
                : null
            }
            options={reasons}
            onChange={event => {
              onLookupPropertyChanged('reasonId', 'reasonName', event?.reasonId, event?.reasonName, index, 'reasons');
            }}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

CancelOffersListItemComponent.propTypes = {
  documentTypes: PropTypes.array.isRequired,
  reasons: PropTypes.array,
  referenceDates: PropTypes.array.isRequired,
  item: PropTypes.object,
  index: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPropertyChanged: PropTypes.func.isRequired,
  onLookupPropertyChanged: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired
};
export default CancelOffersListItemComponent;
